import { CompetitionTypeSelect } from '~/pages/competition/components/competition-type-select';
import { CategoryMultiSelect } from '~/pages/categories/components/category-multi-select';
import { scoringSystemNameToLabel } from '~/utils/scoring-system';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { genderNameToLabel, genderOptions } from '~/utils/gender';
import { GenderEnum, ScoringSystemEnum } from '~/apis/ssf';
import { Select } from '~/components/Input/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema, Schema } from './division-form.schema';
import { Button } from '~/components/Button/Button';
import { Fragment } from 'react/jsx-runtime';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';

interface Props {
  onSubmit: (data: Schema) => void;
  mode: 'create' | 'edit';
  defaultValues?: Partial<Schema>;
  isLoading: boolean;
}

const scoringSystemOptions: ScoringSystemEnum[] = ['IPFGL', 'IPFGL_MALE'];

export function CompetitionDivisionForm({ defaultValues, mode, isLoading, onSubmit }: Props) {
  const { formState, register, handleSubmit, setValue, watch, trigger } = useForm<Schema>({
    defaultValues: defaultValues as Schema,
    resolver: zodResolver(schema),
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  const isMixed = watch('gender') === 'B';

  function handleFormSubmit(data: Schema) {
    onSubmit({
      ...data,
      min_female_lifters: isMixed ? data.min_female_lifters : 0,
      min_male_lifters: isMixed ? data.min_male_lifters : 0,
    });
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="name" label="Namn">
            <Input.Field type="text" {...register('name')} formState={formState} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="competition_type" label="Tävlingstyp">
            <CompetitionTypeSelect
              {...register('competition_type')}
              onChange={event => {
                setValue('competition_type', Number(event.target.value), { shouldDirty: true });
                setValue('age_categories', [], { shouldDirty: true });
                trigger('competition_type');
              }}
              value={watch('competition_type')}
              formState={formState}
              disabled={isLoading}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="age_categories" label="Ålderskategorier">
            <CategoryMultiSelect
              {...register('age_categories')}
              query={{ competition_types: watch('competition_type') ? [watch('competition_type')] : [] }}
              onChange={values => {
                setValue('age_categories', values, { shouldDirty: true });
                trigger('age_categories');
              }}
              value={watch('age_categories')}
              formState={formState}
              disabled={isLoading}
            />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="gender" label="Kön">
            <Select
              {...register('gender')}
              placeholder="Välj kön"
              formState={formState}
              options={genderOptions.map(value => [
                genderNameToLabel(value as GenderEnum, { mixedLabel: 'Mixad' }),
                value,
              ])}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group
            formState={formState}
            htmlFor="scoring_system"
            label="Poängsystem"
            tooltip={
              'IPF GL för damtävlingar och mixade tävlingar\n' +
              'IPF GL Herr för herrtävlingar där damer får delta med herrkoefficient'
            }
          >
            <Select
              {...register('scoring_system')}
              placeholder="Välj poängsystem"
              formState={formState}
              options={scoringSystemOptions.map(value => [scoringSystemNameToLabel(value), value])}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="nomination_start_date" label="Nominering öppnar">
            <Input.Field type="date" {...register('nomination_start_date')} formState={formState} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6 }}>
          <Input.Group formState={formState} htmlFor="nomination_end_date" label="Nominering stänger">
            <Input.Field type="date" {...register('nomination_end_date')} formState={formState} />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: isMixed ? 3 : 6 }}>
          <Input.Group formState={formState} htmlFor="lifters_per_team" label="Lyftare per lag">
            <Input.Field type="number" min="0" {...register('lifters_per_team')} formState={formState} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: isMixed ? 3 : 6 }}>
          <Input.Group formState={formState} htmlFor="reserve_slots" label="Max antal reserver">
            <Input.Field type="number" min="0" {...register('reserve_slots')} formState={formState} />
          </Input.Group>
        </Grid.Item>

        {isMixed && (
          <Fragment>
            <Grid.Item cols={{ sm: 12, md: 3 }}>
              <Input.Group formState={formState} htmlFor="min_female_lifters" label="Minst antal damer">
                <Input.Field type="number" min="0" {...register('min_female_lifters')} formState={formState} />
              </Input.Group>
            </Grid.Item>

            <Grid.Item cols={{ sm: 12, md: 3 }}>
              <Input.Group formState={formState} htmlFor="min_male_lifters" label="Minst antal herrar">
                <Input.Field type="number" min="0" {...register('min_male_lifters')} formState={formState} />
              </Input.Group>
            </Grid.Item>
          </Fragment>
        )}
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
